export type CsvStorageKind =
  | 'progress'
  | 'ignored-warnings'
  | 'verified-import'
  | 'reached-step'
  | 'keep-order';

export type CsvStorageScope = {
  userId: string;
  dealId: string;
  importId?: string;
};

export const CSV_STORAGE_MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

const PREFIX = 'csv-offer:';

const storageKey = (kind: CsvStorageKind, scope: CsvStorageScope) =>
  `${PREFIX}${kind}:${scope.userId}:${scope.dealId}${
    scope.importId ? `:${scope.importId}` : ''
  }`;

const dealPrefix = (userId: string, dealId: string) => `:${userId}:${dealId}`;

type Stored = { v: unknown; at: number };

const isStored = (value: unknown): value is Stored =>
  typeof value === 'object' &&
  value !== null &&
  'v' in value &&
  typeof (value as { at?: unknown }).at === 'number';

const parse = (raw: string | null): Stored | null => {
  if (raw === null) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    return isStored(parsed) ? parsed : null;
  } catch {
    return null;
  }
};

export const readCsvStorage = (
  kind: CsvStorageKind,
  scope: CsvStorageScope
): unknown => {
  try {
    const stored = parse(localStorage.getItem(storageKey(kind, scope)));
    if (!stored || Date.now() - stored.at > CSV_STORAGE_MAX_AGE_MS) return null;
    return stored.v;
  } catch {
    return null;
  }
};

export const writeCsvStorage = (
  kind: CsvStorageKind,
  scope: CsvStorageScope,
  value: unknown
): void => {
  try {
    localStorage.setItem(
      storageKey(kind, scope),
      JSON.stringify({ v: value, at: Date.now() } satisfies Stored)
    );
  } catch {
    return;
  }
};

export const removeCsvStorage = (
  kind: CsvStorageKind,
  scope: CsvStorageScope
): void => {
  try {
    localStorage.removeItem(storageKey(kind, scope));
  } catch {
    return;
  }
};

const csvKeys = (): string[] => {
  const keys: string[] = [];
  for (let index = 0; index < localStorage.length; index++) {
    const key = localStorage.key(index);
    if (key?.startsWith(PREFIX)) keys.push(key);
  }
  return keys;
};

export const purgeCsvDealStorage = (userId: string, dealId: string): void => {
  try {
    const scope = dealPrefix(userId, dealId);
    for (const key of csvKeys()) {
      const rest = key.slice(key.indexOf(':', PREFIX.length));
      if (rest === scope || rest.startsWith(`${scope}:`)) {
        localStorage.removeItem(key);
      }
    }
  } catch {
    return;
  }
};

export const purgeExpiredCsvStorage = (now = Date.now()): void => {
  try {
    for (const key of csvKeys()) {
      const stored = parse(localStorage.getItem(key));
      if (!stored || now - stored.at > CSV_STORAGE_MAX_AGE_MS) {
        localStorage.removeItem(key);
      }
    }
  } catch {
    return;
  }
};
